import { useSelector } from 'react-redux';
import type { RootState } from '../store';

/**
 * Mirrors the permission logic in the web app's useRoutes.js.
 *
 * Account-level permissions come from `state.session.account.permissions` —
 * this is the `am.permissions` column joined in the get-accounts query
 * (account_members table), exactly as the web reads it.
 *
 * For paid-feature gating (plan value, subscription), use the usePaidPlan() hook
 * (hasPaidPlan, planName, features) so behavior matches the web's PaidComponent and route.paid.
 *
 * Project-level permissions come from each project's `users` array, finding
 * the current user by cognito_id and reading their `project_permissions`.
 */
export const usePermissions = () => {
  const account = useSelector((state: RootState) => state.session.account);
  const projects = useSelector((state: RootState) => state.projects.list);
  const currentUser = useSelector((state: RootState) => state.session.last_logged_in);

  const accountPermissions: string[] = Array.isArray(account?.permissions)
    ? account.permissions
    : [];
  const kbPermissions: string[] = Array.isArray(account?.kb_permissions)
    ? account.kb_permissions
    : [];

  /** True if the user has the given account-level permission. */
  const hasAccount = (perm: string): boolean =>
    accountPermissions.includes(perm);

  /** True if the user has any of the given account-level permissions. */
  const hasAnyAccount = (...perms: string[]): boolean =>
    perms.some((p) => accountPermissions.includes(p));

  /**
   * Returns the project-level permissions for a specific project.
   * Looks up the current user inside project.users[] by cognito_id.
   */
  const projectPermissionsFor = (projectId: number | string): string[] => {
    // Direct O(1) lookup — projects are keyed by their ID string in Redux.
    const project = (projects as any)[String(projectId)];
    if (!project || !currentUser?.cognito_id) return [];
    const member = project.users?.find(
      (u: any) => u.cognito_id === currentUser.cognito_id
    );
    return Array.isArray(member?.project_permissions)
      ? member.project_permissions
      : [];
  };

  /** True if the user has the given permission in a specific project. */
  const hasProject = (projectId: number | string, perm: string): boolean =>
    projectPermissionsFor(projectId).includes(perm);

  /** True if the user has any of the given permissions in a specific project. */
  const hasAnyProject = (
    projectId: number | string,
    ...perms: string[]
  ): boolean => perms.some((p) => hasProject(projectId, p));

  // ─── Convenience flags (mirror the web route permission sets) ─────────────
  return {
    accountPermissions,
    kbPermissions,
    hasAccount,
    hasAnyAccount,
    projectPermissionsFor,
    hasProject,
    hasAnyProject,

    // Account-level
    isEleplanAdmin: hasAccount('eleplan-admin'),
    isOwner: hasAccount('owner'),
    isAdministrator: hasAnyAccount('owner', 'administrator'),
    canChat: hasAnyAccount('account-chat', 'basic-user'),
    canManageMembers: hasAnyAccount('owner', 'administrator', 'people'),
    canViewDocuments: hasAnyAccount('owner', 'administrator', 'documents'),
    canManageBilling: hasAccount('billing'),
    hasBasicAccess: hasAccount('basic-user'),
  };
};
